import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const TIPOS_ROL    = ['directivo', 'gerencia', 'operativo', 'externo', 'otro']
const PRIORIDADES  = ['alta', 'media', 'baja']
const ESTADOS_ENT  = ['Pendiente', 'Agendada', 'Realizada']

const TIPO_LABELS = {
  directivo:  { label: 'Directivo',   color: '#7C3AED', bg: '#EDE9FE' },
  gerencia:   { label: 'Gerencia',    color: '#1D4ED8', bg: '#DBEAFE' },
  operativo:  { label: 'Operativo',   color: '#065F46', bg: '#D1FAE5' },
  externo:    { label: 'Externo',     color: '#92400E', bg: '#FEF3C7' },
  otro:       { label: 'Otro',        color: '#6B7280', bg: '#F3F4F6' },
}

const PRIORIDAD_COLORS = {
  alta:  { color: '#EF4444', bg: '#FEE2E2' },
  media: { color: '#F59E0B', bg: '#FEF3C7' },
  baja:  { color: '#10B981', bg: '#D1FAE5' },
}

const ESTADO_ENT_COLORS = {
  Pendiente: { color: '#92400E', bg: '#FEF3C7' },
  Agendada:  { color: '#1E40AF', bg: '#DBEAFE' },
  Realizada: { color: '#065F46', bg: '#D1FAE5' },
}

const TEMPLATE_PREGUNTAS = {
  directivo: [
    '¿Cuál es su visión para la empresa en los próximos 3-5 años?',
    '¿Cuáles son los principales retos estratégicos que enfrenta hoy?',
    '¿Qué capacidades considera que son ventajas competitivas reales?',
    '¿Qué decisiones estratégicas están pendientes o bloqueadas?',
    '¿Cómo describiría la cultura organizacional actual?',
    '¿Qué resultados esperaría ver al finalizar este proceso de consultoría?',
  ],
  gerencia: [
    '¿Cuáles son los principales cuellos de botella en su área?',
    '¿Qué métricas utiliza para medir el desempeño de su equipo?',
    '¿Cómo se alinea su área con los objetivos corporativos?',
    '¿Qué recursos o capacidades le hacen falta para cumplir sus metas?',
    '¿Qué iniciativas de mejora están en marcha actualmente?',
    '¿Cómo es la coordinación con otras áreas?',
  ],
  operativo: [
    '¿Cuáles son los procesos más críticos de su día a día?',
    '¿Dónde ocurren los mayores reprocesos o desperdicios?',
    '¿Qué herramientas o sistemas utiliza en su trabajo?',
    '¿Qué dificultades encuentra al comunicar problemas hacia arriba?',
    '¿Hay prácticas informales que funcionan bien pero no están documentadas?',
  ],
  externo: [
    '¿Cuál es su percepción de la empresa desde afuera?',
    '¿Cómo describiría la propuesta de valor de la empresa?',
    '¿Qué diferencia a esta empresa de sus competidores?',
    '¿Qué mejoras esperaría como cliente/aliado/proveedor?',
  ],
  otro: [
    '¿Cuál es su relación con la organización?',
    '¿Qué perspectiva única puede aportar al proceso?',
    '¿Qué oportunidades o riesgos percibe desde su posición?',
  ],
}

export default function StakeholdersTab({ consultancyId }) {
  const [stakeholders, setStakeholders] = useState([])
  const [loading, setLoading]           = useState(true)
  const [showAdd, setShowAdd]           = useState(false)
  const [form, setForm]                 = useState({ tipo_rol: 'gerencia', prioridad: 'media', estado_entrevista: 'Pendiente' })
  const [saving, setSaving]             = useState(false)
  const [guideFor, setGuideFor]         = useState(null)   // stakeholder id showing guide modal
  const [editingId, setEditingId]       = useState(null)
  const [editForm, setEditForm]         = useState({})

  useEffect(() => {
    if (consultancyId) fetchStakeholders()
  }, [consultancyId])

  async function fetchStakeholders() {
    setLoading(true)
    const { data } = await supabase
      .from('stakeholders')
      .select('*')
      .eq('consultancy_id', consultancyId)
      .order('created_at')
    setStakeholders(data || [])
    setLoading(false)
  }

  async function addStakeholder() {
    if (!form.nombre?.trim()) return
    setSaving(true)
    const { data } = await supabase
      .from('stakeholders')
      .insert({ ...form, consultancy_id: consultancyId })
      .select()
      .single()
    setSaving(false)
    if (data) {
      setStakeholders(prev => [...prev, data])
      setForm({ tipo_rol: 'gerencia', prioridad: 'media', estado_entrevista: 'Pendiente' })
      setShowAdd(false)
    }
  }

  async function updateField(id, updates) {
    await supabase
      .from('stakeholders')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
    setStakeholders(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s))
  }

  async function saveEdit() {
    const { data } = await supabase
      .from('stakeholders')
      .update({ ...editForm, updated_at: new Date().toISOString() })
      .eq('id', editingId)
      .select()
      .single()
    if (data) setStakeholders(prev => prev.map(s => s.id === editingId ? data : s))
    setEditingId(null)
  }

  async function deleteStakeholder(id) {
    if (!confirm('¿Eliminar este stakeholder?')) return
    await supabase.from('stakeholders').delete().eq('id', id)
    setStakeholders(prev => prev.filter(s => s.id !== id))
  }

  // Stats
  const total     = stakeholders.length
  const realizada = stakeholders.filter(s => s.estado_entrevista === 'Realizada').length
  const pct       = total > 0 ? Math.round((realizada / total) * 100) : 0

  if (loading) return <div style={{ padding: 32, color: '#9CA3AF', textAlign: 'center' }}>Cargando stakeholders…</div>

  return (
    <div>
      {/* Header stats */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 20, padding: '12px 16px',
        background: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB', alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Entrevistas realizadas</div>
          <div style={{ height: 8, background: '#E5E7EB', borderRadius: 99 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#7C3AED', borderRadius: 99, transition: 'width 0.4s' }} />
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#7C3AED' }}>{realizada}/{total}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>Realizadas</div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAdd(s => !s)} style={{
          padding: '7px 16px', borderRadius: 8, border: 'none',
          background: '#7C3AED', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}>+ Agregar Stakeholder</button>
      </div>

      {/* Add form */}
      {showAdd && (
        <AddForm
          form={form}
          setForm={setForm}
          onSave={addStakeholder}
          onCancel={() => setShowAdd(false)}
          saving={saving}
        />
      )}

      {/* Table */}
      {stakeholders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
          <div>No hay stakeholders registrados</div>
        </div>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                {['Nombre / Cargo', 'Rol', 'Prioridad', 'Estado entrevista', 'Fecha', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stakeholders.map((s, idx) => (
                editingId === s.id ? (
                  <tr key={s.id} style={{ background: '#F0F9FF', borderBottom: '1px solid #BAE6FD' }}>
                    <td colSpan={6} style={{ padding: '10px 14px' }}>
                      <AddForm
                        form={editForm}
                        setForm={setEditForm}
                        onSave={saveEdit}
                        onCancel={() => setEditingId(null)}
                        saving={false}
                        isEdit
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={s.id} style={{
                    background: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                    borderBottom: '1px solid #F3F4F6'
                  }}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{s.nombre}</div>
                      {s.cargo && <div style={{ fontSize: 11, color: '#6B7280' }}>{s.cargo}</div>}
                      {s.area && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{s.area}</div>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Chip label={TIPO_LABELS[s.tipo_rol]?.label || s.tipo_rol} color={TIPO_LABELS[s.tipo_rol]?.color} bg={TIPO_LABELS[s.tipo_rol]?.bg} />
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Chip label={s.prioridad} color={PRIORIDAD_COLORS[s.prioridad]?.color} bg={PRIORIDAD_COLORS[s.prioridad]?.bg} />
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <select
                        value={s.estado_entrevista}
                        onChange={e => updateField(s.id, { estado_entrevista: e.target.value })}
                        style={{
                          padding: '3px 8px', borderRadius: 99, border: 'none', fontSize: 11,
                          fontWeight: 600, cursor: 'pointer',
                          background: ESTADO_ENT_COLORS[s.estado_entrevista]?.bg || '#F3F4F6',
                          color: ESTADO_ENT_COLORS[s.estado_entrevista]?.color || '#374151',
                        }}
                      >
                        {ESTADOS_ENT.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>
                      {s.fecha_entrevista
                        ? new Date(s.fecha_entrevista + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={() => setGuideFor(s)}
                          title="Ver guía de entrevista"
                          style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 11, cursor: 'pointer' }}
                        >📋</button>
                        <button
                          onClick={() => { setEditingId(s.id); setEditForm({ ...s }) }}
                          style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 11, cursor: 'pointer' }}
                        >✏️</button>
                        <button
                          onClick={() => deleteStakeholder(s.id)}
                          style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', fontSize: 11, cursor: 'pointer', color: '#EF4444' }}
                        >✕</button>
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Interview guide modal */}
      {guideFor && (
        <GuideModal stakeholder={guideFor} onClose={() => setGuideFor(null)} />
      )}
    </div>
  )
}

function Chip({ label, color, bg }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      color: color || '#374151', background: bg || '#F3F4F6'
    }}>{label}</span>
  )
}

function AddForm({ form, setForm, onSave, onCancel, saving, isEdit }) {
  const inp = { padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' }
  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 12 }}>{isEdit ? 'Editar Stakeholder' : 'Nuevo Stakeholder'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px 10px', marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Nombre *</label>
          <input value={form.nombre || ''} onChange={e => setForm(f => ({...f, nombre: e.target.value}))} style={{ ...inp, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Cargo</label>
          <input value={form.cargo || ''} onChange={e => setForm(f => ({...f, cargo: e.target.value}))} style={{ ...inp, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Área</label>
          <input value={form.area || ''} onChange={e => setForm(f => ({...f, area: e.target.value}))} style={{ ...inp, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Email</label>
          <input type="email" value={form.email || ''} onChange={e => setForm(f => ({...f, email: e.target.value}))} style={{ ...inp, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Tipo de rol</label>
          <select value={form.tipo_rol || 'otro'} onChange={e => setForm(f => ({...f, tipo_rol: e.target.value}))} style={{ ...inp, width: '100%' }}>
            {TIPOS_ROL.map(t => <option key={t} value={t}>{TIPO_LABELS[t]?.label || t}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Prioridad</label>
          <select value={form.prioridad || 'media'} onChange={e => setForm(f => ({...f, prioridad: e.target.value}))} style={{ ...inp, width: '100%' }}>
            {PRIORIDADES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Estado entrevista</label>
          <select value={form.estado_entrevista || 'Pendiente'} onChange={e => setForm(f => ({...f, estado_entrevista: e.target.value}))} style={{ ...inp, width: '100%' }}>
            {ESTADOS_ENT.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Fecha entrevista</label>
          <input type="date" value={form.fecha_entrevista || ''} onChange={e => setForm(f => ({...f, fecha_entrevista: e.target.value || null}))} style={{ ...inp, width: '100%' }} />
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Notas</label>
        <textarea rows={2} value={form.notas || ''} onChange={e => setForm(f => ({...f, notas: e.target.value}))} style={{ ...inp, width: '100%', resize: 'vertical' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid #D1D5DB', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
        <button onClick={onSave} disabled={saving} style={{
          padding: '7px 16px', borderRadius: 7, border: 'none',
          background: saving ? '#C4B5FD' : '#7C3AED', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer'
        }}>{saving ? 'Guardando…' : isEdit ? 'Guardar' : 'Agregar'}</button>
      </div>
    </div>
  )
}

function GuideModal({ stakeholder, onClose }) {
  const preguntas = TEMPLATE_PREGUNTAS[stakeholder.tipo_rol] || TEMPLATE_PREGUNTAS.otro
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 28, maxWidth: 560, width: '100%',
        maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Guía de Entrevista</div>
            <div style={{ fontSize: 13, color: '#6B7280' }}>
              {stakeholder.nombre}
              {stakeholder.cargo && ` · ${stakeholder.cargo}`}
              <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 99, background: TIPO_LABELS[stakeholder.tipo_rol]?.bg, color: TIPO_LABELS[stakeholder.tipo_rol]?.color, fontSize: 11, fontWeight: 600 }}>
                {TIPO_LABELS[stakeholder.tipo_rol]?.label || stakeholder.tipo_rol}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 18, color: '#6B7280' }}>✕</button>
        </div>

        <div style={{ marginBottom: 12, fontSize: 12, color: '#9CA3AF' }}>
          Preguntas sugeridas — adaptar según el contexto de la reunión
        </div>

        <ol style={{ margin: 0, padding: '0 0 0 18px' }}>
          {preguntas.map((q, i) => (
            <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.5, marginBottom: 10 }}>{q}</li>
          ))}
        </ol>

        <div style={{ marginTop: 20, padding: '12px 14px', background: '#F9FAFB', borderRadius: 8, fontSize: 12, color: '#6B7280' }}>
          💡 Recuerda: el objetivo es comprender la perspectiva del stakeholder, no seguir el guión al pie de la letra. Escucha activamente y profundiza en los temas que emerjan naturalmente.
        </div>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#7C3AED', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}
